import type { Month } from './dates.js';
import { round2 } from './money.js';
import type { EffectiveMovement, EffectivePart } from './attribution.js';
import type { BudgetRow, Category } from './model.js';

export interface Attributed { category: string; month: Month | ''; kind: string; attributable: number }

/** Movimientos y partes como una sola lista de importes atribuibles (lo que suman las fórmulas del Resumen). */
export function attributedItems(movements: EffectiveMovement[], parts: EffectivePart[]): Attributed[] {
  return movements.map(m => ({ category: m.effCategory, month: m.effMonth, kind: m.kind, attributable: m.attributable }))
    .concat(parts.map(p => ({ category: p.category, month: p.effMonth, kind: p.kind, attributable: p.attributable })));
}

export interface MonthTotals { income: number; expenses: number; savings: number; savingsRate: number | null }

export function monthTotals(items: Attributed[], month: Month): MonthTotals {
  let income = 0, expenses = 0;
  for (const it of items) {
    if (it.month !== month) continue;
    if (it.kind === 'income') income += it.attributable;
    else if (it.kind === 'expense') expenses += it.attributable;
  }
  income = round2(income); expenses = round2(expenses);
  const savings = round2(income + expenses);
  return { income, expenses, savings, savingsRate: income > 0 ? savings / income : null };
}

/** Total atribuible por categoría en un mes. */
export function categoryTotals(items: Attributed[], month: Month): Map<string, number> {
  const out = new Map<string, number>();
  for (const it of items) {
    if (it.month !== month || !it.attributable) continue;
    out.set(it.category, round2((out.get(it.category) ?? 0) + it.attributable));
  }
  return out;
}

/** Presupuesto de una categoría en un mes: el del mes si está, si no el mensual; null si no hay fila. */
export function budgetFor(rows: BudgetRow[], year: number, monthIndex: number, category: string): number | null {
  const row = rows.find(r => r.year === year && r.category === category);
  if (!row) return null;
  const o = row.overrides[monthIndex];
  return o === null || o === undefined ? row.monthly : o;
}

export interface BudgetLine { category: string; budget: number | null; spent: number; left: number | null; used: number | null; warn: boolean; over: boolean }

/** Presupuesto frente a real (gasto positivo) para las categorías de gasto, como la tabla del Resumen. */
export function budgetReport(items: Attributed[], rows: BudgetRow[], categories: Category[], month: Month): BudgetLine[] {
  const year = Number(month.slice(0, 4)), mi = Number(month.slice(5, 7)) - 1;
  const totals = categoryTotals(items, month);
  return categories.filter(c => c.kind === 'expense').map(c => {
    const budget = budgetFor(rows, year, mi, c.name);
    const spent = round2(-(totals.get(c.name) ?? 0));
    const used = budget === null ? null : budget === 0 ? (spent > 0 ? 9.99 : 0) : spent / budget;
    const row = rows.find(r => r.year === year && r.category === c.name);
    return { category: c.name, budget, spent, left: budget === null ? null : round2(budget - spent), used, warn: !!row?.warn, over: used !== null && used > 1 };
  });
}
